import { CSSProperties, ChangeEvent, forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import { Button, LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import classNames from "classnames";

import { useId } from "../../hooks/useId";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { CharacterCount } from "../shared/CharacterCount";
import { normalizeAutocompleteValue } from "../utils/autocompleteHelpers";
import { getStyleModeSafeProps } from "../utils/getStyleModeSafeProps";
import { Overlay } from "./Overlay";
import { Placeholder } from "./Placeholder";
import { StyledNativeInput } from "./StyledNativeInput";
import { TextboxProps } from "./Textbox";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedTextboxStyles = createComponentStyleHook<TextboxInputProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "textbox",
});

interface BaseInputProps extends TextboxProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

/**
 * The `Input` component properties.
 */
export interface InputProps extends TextboxProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * The input type.
   */
  readonly type?: "text" | "password";
}

/**
 * The `Input` component properties.
 */

export type TextboxInputProps =
  | (BaseInputProps & {
      /**
       * The input type.
       */
      readonly type: "text";
    })
  | (BaseInputProps & {
      /**
       * The input type.
       */
      readonly type: "password";
      /**
       * Whether to show the password visibility toggle button.
       * This prop is only applicable when `type` is set to `"password"`.
       * If not provided, the toggle will be shown by default.
       */
      readonly showToggle?: boolean;
    });

/**
 * The base input for textboxes.
 */
export const Input = forwardRef<HTMLInputElement, TextboxInputProps>(function Input(props, ref) {
  const {
    type = "text",
    label,
    tooltip,
    instructions,
    hideInstructions,
    hideLabel,
    prefix,
    suffix,
    defaultValue = null,
    required = false,
    autoFocus,
    placeholder,
    maxLength,
    tabIndex,
    onChange,
    onFocusEnter,
    onFocusExit,
    autoComplete,
  } = props;

  const showToggle = type === "password" && ("showToggle" in props && props.showToggle !== undefined ? props.showToggle : true);
  const { mode, styles, StyleModeBoundary } = useResolvedTextboxStyles(getStyleModeSafeProps(props));

  const { value = defaultValue } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Textbox, "textbox"],
    testIdPrefix: "textbox",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const [prefixSize, setPrefixSize] = useState<number | null>(null);
  const [suffixSize, setSuffixSize] = useState<number | null>(null);
  const [isMasked, setIsMasked] = useState<boolean>(type === "password");

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(ev.target.value, value);
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
  }, [onFocusExit]);

  // Ensure that if the component becomes disabled while unmasked, it is masked again to prevent password exposure.
  useEffect(() => {
    if (disabled && type === "password") {
      setIsMasked(true);
    }
  }, [disabled, type]);

  const handleToggle = useCallback(() => {
    setIsMasked((masked) => !masked);
  }, []);

  const overlayLayoutHandler = useCallback((prefixSize: number | null, suffixSize: number | null) => {
    setPrefixSize(prefixSize);
    setSuffixSize(suffixSize);
  }, []);

  const characterCount = useMemo(() => value?.length, [value]) || 0;

  const autocompleteValue = useMemo(() => normalizeAutocompleteValue(autoComplete), [autoComplete]);

  const style = useMemo(() => {
    if (prefixSize === null && suffixSize === null) return {};
    const result: CSSProperties = {};
    if (prefixSize !== null) result.paddingLeft = `${prefixSize}px`;
    if (suffixSize !== null) {
      disabled && type === "password" ? (result.paddingRight = `${suffixSize + 40}px`) : (result.paddingRight = `${suffixSize}px`);
    }
    return result;
  }, [prefixSize, suffixSize]);

  const effectiveSuffix = useMemo(() => {
    if (!showToggle) return suffix;
    return (
      <>
        {suffix}
        {!disabled && (
          <Button
            onClick={handleToggle}
            icon={isMasked ? "eye-slash" : "eye"}
            appearance="text"
            disabled={disabled || submitting}
            aria-label={isMasked ? "Hide password" : "Show password"}
          />
        )}
      </>
    );
  }, [suffix, showToggle, isMasked, disabled, submitting, handleToggle]);

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<Placeholder hasInstructions={!!instructions} />}>
        <InputContainer
          testId={baseHtmlProps["data-testid"]}
          className={baseHtmlProps.className}
          style={baseHtmlProps.style}
          id={id}
          label={label}
          instructions={
            (!!instructions || !!maxLength) && (
              <CharacterCount maxLength={maxLength} characterCount={characterCount}>
                {instructions}
              </CharacterCount>
            )
          }
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tooltip={tooltip}
          hideInstructions={hideInstructions}
          hideLabel={hideLabel}
        >
          <div className="input-container">
            <StyledNativeInput
              className={classNames("input", { invalid })}
              data-testid={baseHtmlProps["data-testid"]}
              id={id}
              type={type === "password" && isMasked ? "password" : "text"}
              aria-labelledby={!hideLabel ? `${id}-label` : undefined}
              aria-label={hideLabel ? `${type} input ${id}` : undefined}
              aria-invalid={invalid}
              aria-required={disabled || readOnly ? undefined : required}
              disabled={disabled}
              autoFocus={autoFocus}
              readOnly={readOnly || submitting}
              aria-readonly={readOnly || submitting}
              aria-busy={submitting}
              aria-describedby={instructions ? `${id}-instructions` : undefined}
              aria-errormessage={invalid ? `${id}-error` : undefined}
              ref={ref}
              tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
              placeholder={placeholder}
              style={style}
              value={value ?? ""}
              autoComplete={autocompleteValue}
              onChange={changeHandler}
              onFocus={focusHandler}
              onBlur={blurHandler}
            />
            <Overlay prefix={prefix} suffix={effectiveSuffix} onLayout={overlayLayoutHandler} />
          </div>
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
